import { Badge } from "@knowtex-ai/components";
import { Check } from "iconoir-react";
import { formatDate } from "./foiaUtils";

const STATUS_BADGES = {
  match: { variant: "success", label: "Match" },
  partial: { variant: "warning", label: "Partial" },
  none: { variant: "default", label: "No match" },
};

function matchHint(match) {
  if (match.providerElsewhere) return "Provider name matches, but at a different station";
  if (match.dateDiffDays > 0) {
    return `${match.dateDiffDays} day${match.dateDiffDays === 1 ? "" : "s"} from the email date`;
  }
  return null;
}

function EmailAppointmentRow({ match, emailAppointment }) {
  const badge = STATUS_BADGES[match.status];
  const emailLabel = [emailAppointment.date ? formatDate(emailAppointment.date) : null, emailAppointment.provider]
    .filter(Boolean)
    .join(" — ");
  const hint = matchHint(match);

  return (
    <li className="flex items-start gap-2 text-xs">
      <Badge variant={badge.variant} size="small">
        {badge.label}
      </Badge>
      {match.date ? (
        <span className="text-gray-700">
          {formatDate(match.date)} — {match.provider || "Unknown provider"}
          {match.stationNumber && <span className="text-gray-400"> · Station {match.stationNumber}</span>}
          {hint && <span className="text-gray-400"> · {hint}</span>}
        </span>
      ) : (
        <span className="text-gray-400">No visit found for {emailLabel || "this email appointment"}</span>
      )}
    </li>
  );
}

function StationBadge({ stationMatch, stationNumber }) {
  if (stationMatch === true) {
    return (
      <Badge variant="success" size="small">
        Station {stationNumber} ✓
      </Badge>
    );
  }
  if (stationMatch === false) {
    return (
      <Badge variant="warning" size="small">
        Different station
      </Badge>
    );
  }
  return null;
}

export default function FoiaPatientCandidateCard({ patient, emailAppointments, stationNumber, selected, onSelect }) {
  const showEmailMatches = patient.matches.length > 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(patient)}
      className={`text-left rounded-lg border px-4 py-3 transition-colors ${
        selected ? "border-ktx-purple bg-light-purple/40" : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{patient.name}</span>
          {patient.bestMatch && (
            <Badge variant="success" size="small">
              Best match
            </Badge>
          )}
          <StationBadge stationMatch={patient.stationMatch} stationNumber={stationNumber} />
        </div>
        {selected && <Check width={16} height={16} className="text-ktx-purple" />}
      </div>

      <div className="text-xs text-gray-500 mt-0.5">
        MRN {patient.mrn} · {patient.encounterCount} appointment(s) on file
      </div>

      {showEmailMatches ? (
        <ul className="mt-2 flex flex-col gap-1">
          {patient.matches.map((match) => (
            <EmailAppointmentRow
              key={match.emailIndex}
              match={match}
              emailAppointment={emailAppointments[match.emailIndex] || {}}
            />
          ))}
        </ul>
      ) : (
        patient.providers.length > 0 && (
          <div className="text-xs text-gray-400 mt-1">
            Providers: {patient.providers.join(", ")}
            {patient.stations.length > 0 && <> · Station {patient.stations.join(", ")}</>}
          </div>
        )
      )}
    </button>
  );
}
